<template>
	<div class="plan">
		<div class="panel-list-header">
			<div class="panel-header-item">
				{{ floor.name }}
				<div v-if="!isMobile" class="switchbutton-wrapper">
					<SwitchButton
						class="switchbutton"
						v-for="id in fireProtectionGroupIds"
						:key="id"
						:label="$t(`fire_protection_group.${id}`)"
						:value="selectedGroupId === id"
						@input="toggleGroup(id)"
					/>

				</div>
			</div>
			<div class="level-right" v-if="$isAllowedTo('floor_areas_edit')">
				<div class="edit-button" @click.stop="onEdit">
					<i class="fas fa-edit"></i>
				</div>
			</div>
		</div>
		<div class="columns plan-container">
			<div
				class="column plan-box-container"
				@mousewheel.prevent="zoom"
				@mousedown="mouseDownHandler"
				@mousemove="mouseMoveHandler"
				@mouseup="mouseUpHandler"
				@mouseleave="stopMapMove"
				v-touch:touchhold="newItem"
				v-touch-options="{ touchHoldTolerance: 1000 }"
				@contextmenu.prevent
				:style="`cursor: ${cursor}`"
			>
				<div
					id="dropZone"
					class="box plan-box"
					@contextmenu.prevent
					@dragover.prevent
					@drop.prevent="drop"
					:style="
						`padding-top: ${imgRatio *
							100}%; transform: scale(${zoomLevel}))`
					"
				>
					<img
						:src="this.fileState.file"
						alt=""
						class="image-container"
						@load="updateRatio"
						:draggable="false"
					/>
					<Items
						:items="items"
						:areaWidthRatio="areaWidthRatio"
						:areaHeightRatio="areaHeightRatio"
						:zoomLevel="zoomLevel"
						:disable="disable"
						:flaw-categories="flawCategories"
						@saveMapItem="saveMapItem"
						@deleteMapItem="removeItem"
						@openMenu="openMenu"
						v-model="moveItem"
						:showgroupnumbers="true"
						:showTooltip="false"
					/>
					<Areas
						:areas="areas"
						:areaWidthRatio="areaWidthRatio"
						:areaHeightRatio="areaHeightRatio"
						:moveItem="moveItem"
						:dragging="dragging"
						:disable="disable"
						@saveArea="saveArea"
						@deleteArea="removeArea"
						v-model="makingArea"
					/>
					<MenuComponent
						v-model="showMenu"
						:areaWidthRatio="areaWidthRatio"
						:menuX="menuX"
						:menuY="menuY"
						:item="activeItem"
						:zoomLevel="zoomLevel"
						:isMobile="isMobile"
						@animated="animating = false"
						@saveItem="saveMapItem"
						@sliders="sliders = $event"
						@editItem="editItem"
					/>
				</div>
			</div>
			<div v-if="!isMobile && $isAllowedTo('floor_areas_edit')" class="column is-2 items">
				<div class="item-holder">
					<div
						v-for="fireProtection in reduceFireProtections(fireProtectionsList)"
						:key="fireProtection.id"
						:id="fireProtection.id"
						class="draggable-item item-help"
						:class="getType(fireProtection.inventoryType)?.icon"
						draggable="true"
						@dragstart="dragNew(fireProtection, $event)"
					>
						<span :style="dragging ? 'display: none' : ''" class="help-text">{{
							 preferCategoryLabel(fireProtection) 
						}}</span>
					</div>
				</div>
			</div>
		</div>
		<div class="plan-info" v-if="!isMobile && $isAllowedTo('floor_areas_edit')">
			<p class="plan-info__header">{{ $t('map_drop.tooltip') }}</p>
			<p>{{ $t('map_drop.tooltip_new_area') }}</p>
			<p>{{ $t('map_drop.tooltip_move_area') }}</p>
			<p>{{ $t('map_drop.tooltip_rename_area') }}</p>
			<p>{{ $t('map_drop.tooltip_menu') }}</p>
			<p>{{ $t('map_drop.tooltip_remove') }}</p>
			<p>{{ $t('map_drop.tooltip_split_group') }}</p>
		</div>
	</div>
</template>

<script>
import { mapGetters, mapActions, mapState } from 'vuex';
import iconMixin from '@/mixins/iconMixin.js';
import Areas from './AreaComponent';
import Items from './ItemComponent';
import MenuComponent from './MenuComponent';
import SwitchButton from '@/components/switchButton';
export default {
	mixins: [iconMixin],
	components: {
		Areas,
		Items,
		MenuComponent,
		SwitchButton
	},
	props: {
		fireProtections: Array,
		map: Object,
		floor: Object,
		areas: Array,
		items: Array,
	},
	data() {
		return {
			makingArea: false,
			areaWidthRatio: 1,
			areaHeightRatio: 1,
			createType: {},
			imgRatio: 1,
			dragging: false,
			image: null,
			showMenu: false,
			menuX: 0,
			menuY: 0,
			zoomLevel: 1,
			mapX: 0,
			mapY: 0,
			movingMap: false,
			movedMap: false,
			moveItem: false,
			dragX: 0,
			dragY: 0,
			activeItem: {},
			animating: false,
			sliders: false,
			holdingShift: false,
			holdingCtrl: false,
			hoveringArea: false,
			fireProtectionsList: [],
			selectedGroupId: null 
		};
	},
	watch: {
		map: {
			handler: function() {
				var img = new Image();
				img.onload = () => {
					this.imgRatio = img.height / img.width;
				};
				this.getFile(this.map.file.id).then(() => {
					img.src = this.fileState.file;
				});
			}
		},
		fireProtections: {
			immediate: false,
			handler(newVal) {
			if (this.selectedGroupId === null) {
				this.fireProtectionsList = newVal;
			} else {
				this.fireProtectionsList = newVal.filter(
				fp => fp.fireProtectionType === this.selectedGroupId
				);
			}
			}
		}
	},
	computed: {
		...mapGetters('appStore', ['isMobile']),
		...mapState('fileStore', {
			fileState: state => state
		}),
		...mapState('fireProtectionStore', ['fireExtinguishers']),

		disable() {
			return this.sliders || this.dragging;
		},

		cursor() {
			if (this.$isAllowedTo('floor_areas_edit')) {
				if (this.makingArea || this.holdingShift) return 'crosshair';

				if (this.holdingCtrl && this.hoveringArea) return 'move';
			}

			return this.movingMap ? 'grabbing' : 'grab';
		},
		fireProtectionGroupIds() {
			return [0, 1, 2, 3, 4, 5];
		},
	},

	methods: {
		toggleMenu() {
			this.showMenu = !this.showMenu;
		},
		...mapActions('fileStore', ['getFile']),
		reduceFireProtections(fireProtections) {
			let fireExtinguisherAdded = false;

			return fireProtections.reduce((acc, prot) => {
				if (
					fireExtinguisherAdded && this.fireExtinguishers.some(ext => ext === prot.inventoryType)
				)
					return acc;

				if (this.fireExtinguishers.some(ext => ext === prot.inventoryType))
					fireExtinguisherAdded = true;

				acc.push(prot);

				return acc;
			}, []);
		},
		toggleGroup(id) {
			this.selectedGroupId = (this.selectedGroupId === id) ? null : id;

			if (this.selectedGroupId === null) {
			this.fireProtectionsList = this.fireProtections;
			} else {
			this.fireProtectionsList = this.fireProtections.filter(
				fp => fp.fireProtectionType === this.selectedGroupId
			);
			}
		},
		getFireProtectionName(name) {
			return this.fireExtinguishers.some(ext => ext === name)
				? this.$t('map_drop.fire_extinguisher')
				: name;
		},
		onDelete() {
			this.$emit('delete', this.map);
		},
		onEdit() {
			this.$emit('edit', this.map);
		},
		dragNew(type, ev) {
			ev.dataTransfer.setData('text', ev.target.id);
			this.createType = type;
			this.dragging = true;
			const iconSize = ev.target.offsetWidth / 2;
			this.dragX = iconSize - ev.offsetX;
			this.dragY = ev.offsetY - iconSize;
		},
		drop(ev) {
			if (this.dragging) {
				const dropZone = document.getElementById('dropZone');
				const dropZoneHeight = dropZone.offsetHeight / this.areaHeightRatio;
				let offsetX = (ev.offsetX + this.dragX) / this.areaWidthRatio;
				let offsetY = dropZoneHeight - (ev.offsetY - this.dragY) / this.areaHeightRatio;
				this.items.push({
					fireProtectionId: this.createType.id,
					fireProtection: this.createType,
					positionX: parseInt(offsetX),
					positionY: parseInt(offsetY),
					scale: 1,
					positionNumber: this.items.length + 1
				});
				this.saveMapItem(this.items[this.items.length - 1]);
				this.dragging = false;
			}
		},
		saveMapItem(item) {
			if (!item.fireProtectionId)
				item.fireProtectionId = item.fireProtection.id;

			item.mapId = this.map.id;
			this.$emit('saveMapItem', item);
		},
		updateRatio() {
			let dropzone = document.getElementById('dropZone');

			if (dropzone) {
				this.areaWidthRatio = dropzone.offsetWidth / 1000;
				this.areaHeightRatio = dropzone.offsetHeight / 1000;
			}
		},
		removeItem(item) {
			this.$emit('deleteMapItem', item);
		},
		editItem(item) {
			this.$emit('editMapItem', item);
		},
		newItem(event) {
			console.log('event', event);
			if (event.target && event.target.classList.contains('placed-item')) {
				return;
			}

			if (this.isMobile) {
				const dropZone = document.getElementById('dropZone');
				const dropZoneHeight = dropZone.offsetHeight / this.areaHeightRatio;
				let item = {
					positionX: Math.round(
						(event.touches[0].clientX - 15) / this.areaWidthRatio
					),
					positionY: Math.round(
						dropZoneHeight -
										(event.touches[0].clientY -
						dropZone.getBoundingClientRect().top) /
						this.areaHeightRatio
					)
				};
				this.$emit('newMapItem', item);
			}
		},
		saveArea(area) {
			area.mapId = this.map.id;
			this.$emit('saveArea', area);
		},
		removeArea(area) {
			this.$emit('deleteArea', area);
		},
		cancelEditName() {
			this.editingAreaName = false;
			this.newAreaName = '';
		},
		mouseDownHandler(ev) {
			if (
				!ev.target.tagName.includes('circle') &&
				!ev.target.className.includes('placed-item') &&
				!ev.target.className.includes('resize') &&
				!ev.ctrlKey &&
				!ev.shiftKey
			) {
				this.startMapMove();
			}
		},
		mouseUpHandler() {
			this.stopMapMove();
		},
		mouseMoveHandler(ev) {
			if (this.holdingCtrl && ev.target.id.includes('area')) {
				this.hoveringArea = true;
			} else if (this.holdingCtrl) this.hoveringArea = false;

			if (this.movingMap) {
				this.moveMap(ev);
			}
		},
		startMapMove() {
			if (!this.makingArea) this.movingMap = true;
		},
		async stopMapMove() {
			this.movingMap = false;
			function flushQueue() {
				return new Promise(resolve => setTimeout(resolve, 0));
			}
			await flushQueue().then(() => {
				this.movedMap = false;
			});
		},
		checkMapBounds() {
			const element = document.getElementById('dropZone');
			const zoom = this.zoomLevel > 1
				? this.zoomLevel - (this.zoomLevel - 1) * 0.35
				: this.zoomLevel < 1
					? this.zoomLevel + (1 - this.zoomLevel) * 0.27
					: 1;
			const width = (element.offsetWidth / zoom) * 0.75;
			const height = (element.offsetHeight / zoom) * 0.75;
			this.mapX =
						this.mapX > width ? width : this.mapX < -width ? -width : this.mapX;
			this.mapY =
						this.mapY > height ? width : this.mapY < -height ? -height : this.mapY;
		},
		moveMap(event) {
			this.movedMap = true;
			this.mapX += event.movementX / this.zoomLevel;
			this.mapY += event.movementY / this.zoomLevel;
			this.checkMapBounds();
		},
		zoom(event) {
			let offsetX = 0;
			let offsetY = 0;
			let pageWidth = document.getElementById('dropZone').offsetWidth / 2;
			let pageHeight = document.getElementById('dropZone').offsetHeight / 2;
			let ratio = 1000 / Math.abs(event.deltaY);
			let left = 0;
			let top = 0;

			if (event.srcElement.tagName === 'circle') {
				let menu = document.getElementById('menu');
				left = event.offsetX + menu.offsetLeft - 10;
				top = event.offsetY + menu.offsetTop - 10;
			} else if (
				event.target.className.includes('placed-item') ||
				event.target.className.includes('menu-button') ||
				event.target.className.includes('area')
			) {
				let offsetTop = 0;
				let offsetLeft = 0;
				let element = event.target;

				if (event.target.className.includes('flaw')) {
					offsetLeft = 15;
					offsetTop = -15;
				}

				if (event.target.className.includes('ok')) {
					offsetLeft = 15;
					offsetTop = 45;
				}

				left = element.offsetLeft + event.offsetX + offsetLeft;
				top = element.offsetTop + event.offsetY + offsetTop;
			} else {
				left = event.offsetX;
				top = event.offsetY;
			}

			this.zoomLevel -= event.deltaY * 0.001;
			offsetX = left + this.mapX - pageWidth;
			offsetX = event.deltaY > 0 ? offsetX : -offsetX;
			offsetY = top + this.mapY - pageHeight;
			offsetY = event.deltaY > 0 ? offsetY : -offsetY;

			if (this.zoomLevel < 1) {
				this.zoomLevel = 1;
				offsetX = 0;
				offsetY = 0;
			} else if (this.zoomLevel > 4) {
				this.zoomLevel = 4;
				offsetX = 0;
				offsetY = 0;
			}

			this.mapX += (offsetX / ratio) * (1 / this.zoomLevel);
			this.mapY += (offsetY / ratio) * (1 / this.zoomLevel);
			this.checkMapBounds();
		},
		openMenu(event, item) {
			if (!this.$isAllowedTo('floor_areas_edit')) return;

			let itemId = null;

			if (item) {
				itemId = item.items ? item.items[0].id : item.id;
			}

			if (itemId === this.activeItem.id && this.showMenu) return;

			this.activeItem = {};

			if (this.animating || this.movedMap) {
				return;
			}

			if (!item) {
				this.menuX = event.offsetX;
				this.menuY = event.offsetY;
			} else {
				this.activeItem = item;
				this.menuX = event.target.offsetLeft;
				this.menuY = event.target.offsetTop;
			}

			if (!this.showMenu) {
				this.animating = true;
			}

			this.showMenu = true;
		},
		setButton(ev) {
			if (!ev.repeat) {
				if (ev.key === 'Shift') this.holdingShift = true;

				if (ev.key === 'Control') this.holdingCtrl = true;
			}
		},
		unsetButton(ev) {
			if (!ev.repeat) {
				if (ev.key === 'Shift') this.holdingShift = false;

				if (ev.key === 'Control') this.holdingCtrl = false;
			}
		}
	},
	async created() {
		window.addEventListener('resize', this.updateRatio);
		window.addEventListener('keydown', this.setButton);
		window.addEventListener('keyup', this.unsetButton);

		if (this.map.file !== null) {
			this.image =
						'data:image/jpeg;base64,' + (await this.getFile(this.map.file.id));
		}

		var img = new Image();
		img.onload = () => {
			this.imgRatio = img.height / img.width;
		};
		img.src = this.fileState.file;
		
		this.fireProtectionsList = this.fireProtections;
	},
	updated() {
		this.updateRatio();
	},
	beforeDestroy() {
		window.removeEventListener('resize', this.updateRatio);
		window.removeEventListener('keydown', this.setButton);
		window.removeEventListener('keyup', this.unsetButton);
	}
};
</script>

<style scoped lang="scss">
@import "@/assets/colors.scss";

$border-radius: 1px;
.box.plan-box {
  padding: 0;
  position: relative;
  overflow: hidden;
  background-size: contain;
  background-repeat: no-repeat;
  margin-left: auto;
  margin-right: auto;
  border-radius: $border-radius;
  box-shadow: none;
  -webkit-touch-callout: none; /* iOS Safari */
  -webkit-user-select: none; /* Safari */
  -khtml-user-select: none; /* Konqueror HTML */
  -moz-user-select: none; /* Old versions of Firefox */
  -ms-user-select: none; /* Internet Explorer/Edge */
  user-select: none; /* Non-prefixed version, currently
                                  supported by Chrome, Opera and Firefox */
}
.plan-info {
  pointer-events: none;
  color: $white;
  margin-left: 20px;
  bottom: 0px;
  left: 5px;
  font-size: 14px;
  -webkit-touch-callout: none; /* iOS Safari */
  -webkit-user-select: none; /* Safari */
  -khtml-user-select: none; /* Konqueror HTML */
  -moz-user-select: none; /* Old versions of Firefox */
  -ms-user-select: none; /* Internet Explorer/Edge */
  user-select: none; /* Non-prefixed version, currently
                                  supported by Chrome, Opera and Firefox */
  font-size: 20px;

  &__header {
    font-weight: 800;
  }
}
.plan-box-container {
  max-width: 1500px;
}

.plan-container {
  justify-content: center;
}

.draggable-item {
  background-color: white;
  width: 36px;
  height: 36px;
  display: inline-block;
  cursor: pointer;
  background-size: contain;
  position: relative;
  margin: 2px;
}
.column.is-2.items {
  max-width: 132px;
}
.item-holder {
  width: 120px;
  height: 100%;
  background: $white;
  position: relative;
  border-radius: $border-radius;
  font-size: 0;
}
.image-container {
  width: 100%;
  position: absolute;
  top: 0;
  left: 0;
  pointer-events: none;
  -webkit-touch-callout: none; /* iOS Safari */
  -webkit-user-select: none; /* Safari */
  -khtml-user-select: none; /* Konqueror HTML */
  -moz-user-select: none; /* Old versions of Firefox */
  -ms-user-select: none; /* Internet Explorer/Edge */
  user-select: none; /* Non-prefixed version, currently
                                  supported by Chrome, Opera and Firefox */
}
.plan {
  background-color: $light;
  border-radius: $border-radius;
  padding-bottom: 5px;
  margin-bottom: 10px;
  overflow: hidden;
  
}
.plan .columns {
  margin: 2px;
  padding-bottom: 10px;
}
.plan .columns .column {
  padding: 5px;
}
.panel-list-header {
  padding-top: 10px;
  background-color: $dark;
  color: $white;
  border: none;
}

.item-help {
  position: relative;
  display: inline-block;
}

.item-help .help-text {
  margin: auto;
  font-size: 10px;
  visibility: hidden;
  background-color: $white;
  color: $dark;
  padding: 2px;
  border-radius: 2px;
  position: absolute;
  z-index: 100;
  opacity: 0;
  transition: opacity 0.3s;
  cursor: default;
  top: -5px;
  pointer-events: none;
}
.item-help:hover .help-text {
  visibility: visible;
  opacity: 1;
}

.column.plan-box-container {
  overflow: hidden;
  margin: 0.3rem 0;
  margin-left: 0.3rem;
  background: white;
}

.panel-header-item {
  display: flex;
  justify-content: space-between;
}
.switchbutton-wrapper {
  display: flex;
  flex-flow: row wrap;
  margin-right: 30px;

  .switchbutton {
    font-size: 14px;
    font-weight: 600;
    margin-right: 10px;
    text-align: center;
  }
}
</style>
